// components/contribute/Step4Review.js
import { useState } from 'react'
import './StepComponents.css'
import SolutionWithImageReferences from '../SolutionWithImageReferences'
import PDFPreviewModal from '../PDFPreviewModal'
import PictureAsPdf from '@mui/icons-material/PictureAsPdf'

function Step4Review ({ formData, userEmail }) {
  const [pdfModalOpen, setPdfModalOpen] = useState(false)
  const [currentPdf, setCurrentPdf] = useState(null)

  // Extract storage path from Firebase Storage URL
  const extractStoragePathFromUrl = (url) => {
    if (!url) return null
    try {
      const match = url.match(/\/o\/([^?]+)/)
      if (match && match[1]) {
        return decodeURIComponent(match[1])
      }
      const match2 = url.match(/storage\.googleapis\.com\/[^/]+\/(.+?)(?:\?|$)/)
      if (match2 && match2[1]) {
        return decodeURIComponent(match2[1])
      }
    } catch (e) {
      console.error('Error extracting storage path from URL:', e)
    }
    return null
  }

  const handlePDFClick = (image) => {
    setCurrentPdf({
      filePath: extractStoragePathFromUrl(image.url || image.preview),
      fileName: image.filename || image.name || 'document.pdf',
      pageNumber: 1
    })
    setPdfModalOpen(true)
  }

  return (
    <div className='step-content'>
      <div className='step-header'>
        <h2>Überprüfung</h2>
        <p>Bitte überprüfen Sie Ihre Eingaben vor dem Einreichen</p>
      </div>

      <div className='review-section'>
        <div className='review-card'>
          <h3>Problem-Details</h3>
          <div className='review-item'>
            <span className='review-label'>Gerät:</span>
            <span className='review-value'>{formData.deviceName || '-'}</span>
          </div>
          <div className='review-item'>
            <span className='review-label'>Kategorie:</span>
            <span className='review-value'>
              {formData.category === 'troubleshooting' && 'Troubleshooting'}
              {formData.category === 'error_code' && 'Fehlercode'}
              {formData.category === 'maintenance' && 'Wartung'}
              {formData.category === 'workaround' && 'Workaround'}
              {formData.category === 'general_knowledge' && 'Allgemeines Wissen'}
            </span>
          </div>
          {formData.errorCode && (
            <div className='review-item'>
              <span className='review-label'>Fehlercode:</span>
              <span className='review-value'>{formData.errorCode}</span>
            </div>
          )}
          <div className='review-item'>
            <span className='review-label'>Titel:</span>
            <span className='review-value'>{formData.problemTitle || '-'}</span>
          </div>
          {formData.problemDescription && (
            <div className='review-item'>
              <span className='review-label'>Beschreibung:</span>
              <span className='review-value'>
                {formData.problemDescription}
              </span>
            </div>
          )}
        </div>

        <div className='review-card'>
          <h3>Lösung</h3>
          <div className='review-item'>
            <span className='review-label'>Schritte:</span>
            <div className='review-solution'>
              {formData.solution ? (
                <SolutionWithImageReferences
                  solution={formData.solution}
                  images={formData.images || []}
                />
              ) : (
                '-'
              )}
            </div>
          </div>
          {formData.components && (
            <div className='review-item'>
              <span className='review-label'>Komponenten:</span>
              <span className='review-value'>{formData.components}</span>
            </div>
          )}
          {formData.images && formData.images.length > 0 && (
            <div className='review-item'>
              <span className='review-label'>Bilder/Videos/PDFs:</span>
              <div className='review-images'>
                {formData.images.map((image, index) => {
                  const isVideo = image.mediaType === 'video' || (image.preview || image.url || '').startsWith('data:video/') || (image.filename || image.name || '').match(/\.(mp4|webm|mov)$/i)
                  const isPDF = image.mediaType === 'pdf' || (image.filename || image.name || '').match(/\.pdf$/i) || (image.url || image.preview || '').match(/\.pdf$/i)
                  return (
                    <div key={index} className='review-image-item' onClick={() => isPDF && handlePDFClick(image)} style={{ cursor: isPDF ? 'pointer' : 'default' }}>
                      {isPDF ? (
                        <div style={{ 
                          width: '100%', 
                          height: '100%', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          background: '#f5f5f5',
                          flexDirection: 'column',
                          gap: '8px'
                        }}>
                          <PictureAsPdf style={{ fontSize: '48px', color: '#d32f2f' }} />
                          <span style={{ fontSize: '12px', color: '#666' }}>PDF</span>
                        </div>
                      ) : isVideo ? (
                        <video
                          src={image.preview || image.url}
                          controls
                          preload="metadata"
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : (
                        <img src={image.preview || image.url} alt={image.name} />
                      )}
                      <div className='review-image-name'>{image.name}</div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>

        <div className='review-card'>
          <h3>Zusatzinformationen</h3>
          <div className='review-item'>
            <span className='review-label'>Schwierigkeitsgrad:</span>
            <span className='review-value'>
              {formData.difficulty === 'easy' && '🟢 Einfach'}
              {formData.difficulty === 'medium' && '🟡 Mittel'}
              {formData.difficulty === 'hard' && '🔴 Schwierig'}
            </span>
          </div>
          {formData.estimatedTime && (
            <div className='review-item'>
              <span className='review-label'>Dauer:</span>
              <span className='review-value'>{formData.estimatedTime}</span>
            </div>
          )}
          {formData.toolsRequired && (
            <div className='review-item'>
              <span className='review-label'>Werkzeuge:</span>
              <span className='review-value'>{formData.toolsRequired}</span>
            </div>
          )}
        </div>

        <div className='review-footer'>
          <p>
            ℹ️ Ihr Beitrag wird von einem Administrator geprüft, bevor er für
            alle verfügbar ist.
          </p>
          <p>
            Eingereicht als: <strong>{userEmail}</strong>
          </p>
        </div>
      </div>

      {/* PDF Preview Modal */}
      {pdfModalOpen && currentPdf && (
        <PDFPreviewModal
          source={currentPdf}
          onClose={() => {
            setPdfModalOpen(false)
            setCurrentPdf(null)
          }}
        />
      )}
    </div>
  )
}

export default Step4Review
